/**
 * Avatar style options - styles neutres pour patients (sans visages ni expressions)
 *
 * Options disponibles :
 * 1. 'initials' - Cercles colorés avec initiales (très professionnel) ✅ ACTIF
 * 2. 'icons' - Icônes minimalistes et modernes
 * 3. 'shapes' - Formes géométriques abstraites colorées
 * 4. 'identicon' - Motifs géométriques type GitHub
 * 5. 'rings' - Design circulaire simple et élégant
 * 6. 'bottts' - Robots géométriques neutres
 */
type AvatarStyle = 'initials' | 'icons' | 'shapes' | 'identicon' | 'rings' | 'bottts'

// 👇 Style actif : Initials (cercles colorés avec initiales)
const CURRENT_AVATAR_STYLE: AvatarStyle = 'initials'

/**
 * Generate a consistent avatar based on patient ID and gender
 * This ensures the same patient always gets the same avatar
 */
export const getPatientAvatar = (patientId: string, gender: string): string => {
  const baseUrl = 'https://api.dicebear.com/7.x'

  // Avatar style generators mapping (styles abstraits et neutres pour patients)
  const avatarGenerators: Record<AvatarStyle, (id: string) => string> = {
    // Style 1: Initials - Cercles colorés avec initiales (ACTIF - très professionnel)
    initials: (id: string) =>
      `${baseUrl}/initials/svg?seed=${id}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`,

    // Style 2: Icons - Icônes minimalistes et modernes
    icons: (id: string) =>
      `${baseUrl}/icons/svg?seed=${id}`,

    // Style 3: Shapes - Formes géométriques abstraites colorées
    shapes: (id: string) =>
      `${baseUrl}/shapes/svg?seed=${id}`,

    // Style 4: Identicon - Motifs géométriques uniques type GitHub
    identicon: (id: string) =>
      `${baseUrl}/identicon/svg?seed=${id}`,

    // Style 5: Rings - Design circulaire simple et élégant
    rings: (id: string) =>
      `${baseUrl}/rings/svg?seed=${id}`,

    // Style 6: Bottts - Robots géométriques neutres
    bottts: (id: string) =>
      `${baseUrl}/bottts/svg?seed=${id}`,
  }

  // Get the generator for the current style and apply it
  const generator = avatarGenerators[CURRENT_AVATAR_STYLE]
  return generator(patientId)
}

/**
 * Get initials from patient name for fallback
 */
export const getPatientInitials = (firstName: string, lastName: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''
  return `${first}${last}`
}
